import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle } from "lucide-react";
import { apiClient } from "../services/api";

const inputStyle = {
  backgroundColor: "var(--lm-s1)",
  border: "1px solid var(--lm-border)",
  color: "var(--lm-ink)",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "14px",
  width: "100%",
  outline: "none",
  transition: "border-color 0.15s",
  boxSizing: "border-box",
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true);
    setError("");
    try {
      await apiClient.post("v1/auth/forgot-password", {
        identifier: identifier.trim(),
      });
      setDone(true);
    } catch (err) {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "var(--lm-canvas)" }}>
      <div style={{ width: "100%", maxWidth: "400px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <span style={{ fontSize: "20px", fontWeight: 700, color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>
            <span style={{ color: "#5e6ad2" }}>m</span>awered
          </span>
        </div>

        {/* Card */}
        <div
          style={{
            backgroundColor: "var(--lm-s1)",
            border: "1px solid var(--lm-border)",
            borderRadius: "12px",
            padding: "36px",
          }}
        >
          {done ? (
            /* Success state */
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "rgba(39,166,68,0.1)",
                  marginBottom: "20px",
                }}
              >
                <CheckCircle style={{ width: 24, height: 24, color: "var(--lm-success)" }} />
              </div>
              <h1
                style={{
                  margin: "0 0 8px",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "var(--lm-ink)",
                  letterSpacing: "-0.3px",
                }}
              >
                Email envoyé
              </h1>
              <p style={{ margin: "0 0 28px", fontSize: "14px", color: "var(--lm-ink-2)", lineHeight: 1.6 }}>
                Si un compte correspond à <strong style={{ color: "var(--lm-ink)" }}>{identifier}</strong>, vous
                recevrez un lien de réinitialisation valable 1 heure.
              </p>
              <p style={{ margin: "0 0 28px", fontSize: "13px", color: "var(--lm-ink-3)", lineHeight: 1.5 }}>
                Vérifiez également vos spams si vous ne trouvez pas l'email.
              </p>
              <button
                onClick={() => navigate("/login")}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  backgroundColor: "#5e6ad2",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  border: "none",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#828fff")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                Retour à la connexion
              </button>
            </div>
          ) : (
            /* Form state */
            <form onSubmit={handleSubmit}>
              <h1
                style={{
                  margin: "0 0 8px",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "var(--lm-ink)",
                  letterSpacing: "-0.3px",
                }}
              >
                Mot de passe oublié
              </h1>
              <p style={{ margin: "0 0 28px", fontSize: "14px", color: "var(--lm-ink-2)", lineHeight: 1.6 }}>
                Entrez votre email ou nom d'utilisateur. Nous vous enverrons un lien de réinitialisation.
              </p>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "var(--lm-ink-2)",
                    marginBottom: "6px",
                  }}
                >
                  Email ou nom d'utilisateur
                </label>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="votre@email.com"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#5e6ad2")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--lm-border)")}
                  disabled={loading}
                  autoFocus
                />
              </div>

              {error && (
                <div
                  style={{
                    backgroundColor: "rgba(220,38,38,0.08)",
                    border: "1px solid rgba(220,38,38,0.25)",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    fontSize: "13px",
                    color: "var(--lm-error)",
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !identifier.trim()}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  backgroundColor: loading || !identifier.trim() ? "var(--lm-s2)" : "#5e6ad2",
                  color: loading || !identifier.trim() ? "var(--lm-ink-3)" : "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  border: "1px solid var(--lm-border)",
                  cursor: loading || !identifier.trim() ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "20px",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!loading && identifier.trim()) e.currentTarget.style.backgroundColor = "#828fff";
                }}
                onMouseLeave={(e) => {
                  if (!loading && identifier.trim()) e.currentTarget.style.backgroundColor = "#5e6ad2";
                }}
              >
                {loading && <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} />}
                {loading ? "Envoi en cours…" : "Envoyer le lien"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  color: "var(--lm-ink-3)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--lm-ink-2)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--lm-ink-3)")}
              >
                <ArrowLeft style={{ width: 14, height: 14 }} />
                Retour à la connexion
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
